import { useMemo } from 'react';
import type { ModuleViewProps } from '../../shell/types';
import { makeFeedbackT, type FeedbackKey, type FeedbackLocale } from './i18n';
import { QueueView } from './queue/view';
import { ReportsView } from './reports/view';
import { QrView } from './qr/view';
import { EscalationsView } from './escalations/view';
import { SettingsView } from './settings/view';
import { ArchiveView } from './archive/view';
import { Notice, ModulePage } from '../../shell/kit';
import { useTab } from '../../shell/hooks';
import { CategoryCatalogProvider } from './catalog';

type TabKey = 'queue' | 'escalations' | 'reports' | 'settings' | 'archive' | 'qr';

interface Tab {
  readonly key: TabKey;
  readonly label: FeedbackKey;
  readonly needs: 'view' | 'reports' | 'admin' | 'network';
}

const TABS: readonly Tab[] = [
  { key: 'queue', label: 'feedback.tab.queue', needs: 'view' },
  { key: 'escalations', label: 'feedback.tab.escalations', needs: 'view' },
  { key: 'reports', label: 'feedback.tab.reports', needs: 'reports' },
  { key: 'settings', label: 'feedback.tab.settings', needs: 'view' },
  // Архив без объекта: читает только сетевая роль с правом на обращения.
  { key: 'archive', label: 'feedback.tab.archive', needs: 'network' },
  { key: 'qr', label: 'feedback.tab.qr', needs: 'admin' },
];

const VIEW_ROLES = new Set(['site_manager', 'marketer', 'network_admin', 'auditor']);
const ANSWER_ROLES = new Set(['site_manager', 'marketer', 'network_admin']);
const REPORT_ROLES = new Set(['site_manager', 'marketer', 'network_admin', 'auditor']);

export default function FeedbackModule({ t: shellT, user, siteId, title }: ModuleViewProps) {
  const locale: FeedbackLocale = user.locale === 'kk' ? 'kk' : 'ru';
  const t = useMemo(() => makeFeedbackT(locale), [locale]);

  const roles = useMemo(() => new Set(user.bindings.map((b) => b.role)), [user.bindings]);
  const networkWide = useMemo(() => user.bindings.some((b) => b.siteId === null), [user.bindings]);

  const canView = [...roles].some((r) => VIEW_ROLES.has(r));
  const canAnswer = [...roles].some((r) => ANSWER_ROLES.has(r));
  const canReports = [...roles].some((r) => REPORT_ROLES.has(r));
  const canAdmin = networkWide && roles.has('network_admin');

  const allowed = (tab: Tab): boolean => {
    if (tab.needs === 'view') return canView;
    if (tab.needs === 'reports') return canReports;
    if (tab.needs === 'network') return networkWide && canView;
    return canAdmin;
  };

  const visible = TABS.filter(allowed);
  const [active, setActive] = useTab<TabKey>(
    'feedback',
    visible.map((tab) => tab.key),
    visible[0]?.key ?? 'queue',
  );
  const current = visible.find((tab) => tab.key === active) ?? visible[0];

  return (
    <CategoryCatalogProvider locale={locale}>
    <ModulePage
      title={title}
      subtitle={shellT('shell.nav')}
      tabs={visible.map((tab) => ({ key: tab.key, label: t(tab.label) }))}
      activeTab={current?.key ?? active}
      onTab={setActive}
    >
      {visible.length === 0 ? (
        <Notice tone="warn">{t('feedback.common.forbidden')}</Notice>
      ) : (
        <>
          {current?.key === 'queue' ? (
            <QueueView
              t={t}
              locale={locale}
              canAnswer={canAnswer}
              canTick={canAdmin}
              siteId={siteId}
            />
          ) : null}
          {current?.key === 'escalations' ? (
            <EscalationsView t={t} locale={locale} siteId={siteId} canAnswer={canAnswer} />
          ) : null}
          {current?.key === 'reports' ? <ReportsView t={t} locale={locale} siteId={siteId} /> : null}
          {current?.key === 'settings' ? <SettingsView t={t} locale={locale} canEdit={canAdmin} /> : null}
          {current?.key === 'archive' ? <ArchiveView t={t} /> : null}
          {current?.key === 'qr' ? <QrView t={t} siteId={siteId} /> : null}
        </>
      )}
    </ModulePage>
    </CategoryCatalogProvider>
  );
}
